import type { TFunction } from 'i18next';
import formatDate from 'date-fns/format';

import type { TableColumn } from 'app/components/Table';
import OpponentAvatar from 'app/components/OpponentAvatar';
import VideogameIcon from 'app/components/VideogameIcon';
import Tooltip from 'app/components/Tooltip';
import Button from 'app/components/Button';
import MarketTag from 'app/components/MarketTag';
import plusIcon from 'app/icons/plus.svg';
import Icon from 'app/components/Icon';
import menuIcon from 'app/icons/menu.svg';
import editIcon from 'app/icons/edit.svg';
import ResetedButton from 'app/components/Button/ResetedButton';

import style from './style.scss';

const getBlacklistPlayerTeamAsOpponent = (
  team: NonNullable<BlacklistPlayer['current_team']>
): PandapiTeam | PandapiPlayer => ({
  ...team,
  slug: '',
  first_name: '',
  last_name: '',
  hometown: '',
  role: ''
});

const getStatusTagColor = (status: BlacklistStatus): 'red' | 'orange' | 'dark_grey' => {
  if (status === null) {
    return 'dark_grey';
  }

  return status === 'blacklist' ? 'red' : 'orange';
};

const getBlacklistTableColumns = (
  t: TFunction,
  openHistory: (player: BlacklistPlayer) => void,
  edit: (player: BlacklistPlayer) => void
): TableColumn<BlacklistPlayer>[] => {
  const column: TableColumn<BlacklistPlayer>[] = [
    {
      key: 'name',
      label: t('blacklist-table-player_name'),
      width: 20,
      sortable: true,
      render: player => player.name
    },
    {
      key: 'current_team',
      label: t('blacklist-table-current_team'),
      width: 18,
      sortable: true,
      render: player => {
        if (player.current_team) {
          return (
            <div className={style.playerTeam}>
              <OpponentAvatar
                opponent={getBlacklistPlayerTeamAsOpponent(player.current_team)}
                size={26}
                className={style.playerTeamIcon}
              />{' '}
              {player.current_team.name}
            </div>
          );
        }
        return '-';
      }
    },
    {
      key: 'current_videogame_slug',
      label: t('blacklist-table-current_videogame'),
      width: 12,
      sortable: true,
      render: player => (
        <div className={style.tableCellVideogame}>
          <VideogameIcon slug={player.current_videogame_slug} size={27} />
        </div>
      )
    },
    {
      key: 'status',
      label: t('blacklist-status'),
      width: 12,
      sortable: true,
      render: player =>
        player.status === null ? (
          '-'
        ) : (
          <MarketTag
            color={player.status === 'blacklist' ? 'red' : 'orange'}
            className={style.statusTag}
          >
            {t(`blacklist-status-${player.status}`)}
          </MarketTag>
        )
    },
    {
      key: 'last_modified_at',
      label: t('blacklist-table-last_modified_at'),
      width: 22,
      sortable: true,
      render: player =>
        player.last_modified_at ? formatDate(new Date(player.last_modified_at), 'MM/dd/yyyy') : '-'
    },
    {
      key: 'actions',
      label: t('blacklist-table-actions'),
      width: 16,
      render: player => (
        <div className={style.actionButtonsContainer}>
          {player.last_modified_at !== null && (
            <Tooltip content={t('blacklist-table-actions-history_tooltip')}>
              <ResetedButton
                type="button"
                onClick={e => {
                  e.currentTarget.blur();
                  openHistory(player);
                }}
                className={style.actionButton}
              >
                <Icon href={menuIcon} className={style.actionButtonIcon} />
              </ResetedButton>
            </Tooltip>
          )}
          {player.status !== null ? (
            <Tooltip content={t('blacklist-table-actions-edit_tooltip')}>
              <ResetedButton
                type="button"
                onClick={e => {
                  e.currentTarget.blur();
                  edit(player);
                }}
                className={style.actionButton}
              >
                <Icon href={editIcon} className={style.actionButtonIcon} />
              </ResetedButton>
            </Tooltip>
          ) : (
            <Button
              onClick={e => {
                e.currentTarget.blur();
                edit(player);
              }}
            >
              <Icon href={plusIcon} className={style.addButtonIcon} />{' '}
              {t('blacklist-table-actions-add_to_list')}
            </Button>
          )}
        </div>
      )
    }
  ];

  return column;
};

export { getBlacklistTableColumns, getBlacklistPlayerTeamAsOpponent, getStatusTagColor };
